import React from 'react'
import PropTypes from 'prop-types'
import pluralize from 'pluralize'
import { memoize } from 'lodash'
import {
  majorScale,
  minorScale,
  IconButton,
  Pane,
  Select,
  Text
} from 'evergreen-ui'
import { abbreviateNumber } from '../../../lib/utils'

const layoutProps = {
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center'
}

/**
 * Get a cache key of all arguments stringified
 * Useful since lodash `memoize` only uses the first argument, by default
 */
function cacheKeyResolver(...args) {
  return JSON.stringify(args)
}

function getTotalPages(itemCount = 0, pageSize = 1) {
  return Math.ceil(itemCount / pageSize)
}

/**
 * Get the (memoized) page count from a given item count and page size
 * @param {number} itemCount - the total number of items
 * @param {number} pageSize - the number of items per page
 * @returns {number} - the number of pages produced by this page size and item count
 */
const getTotalPageCount = memoize(getTotalPages, cacheKeyResolver)

/**
 * Get the plural or singular tense of an item name based on the number of items
 * (memoized to reduce redundant pluralization invocations)
 * @param {string} word - the word or name of the items
 * @param {number} itemCount - the number of items
 * @returns {string} - the plural or singular tense of the word
 */
const getItemName = memoize(pluralize, cacheKeyResolver)

class Pagination extends React.PureComponent {
  static propTypes = {
    /**
     * The current page
     */
    currentPage: PropTypes.number,
    /**
     * The total number of items
     */
    itemCount: PropTypes.number,
    /**
     * The singular name of the item
     */
    itemName: PropTypes.string,
    /**
     * Callback prop for requesting a specific page in the set:
     * `function (event, page) {}`
     */
    onRequestPageChange: PropTypes.func.isRequired,
    /**
     * Callback prop for requesting a new page size for the set:
     * `function (event, pageSize) {}`
     * Only applicable when using `variant="full"`
     */
    onRequestPageSizeChange: PropTypes.func,
    /**
     * The number of items per page
     */
    pageSize: PropTypes.number,
    /**
     * The page size options available to select. Only applicable when `showPageSizeOptions: true`
     */
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    /**
     * Whether or not to show the page size select input
     */
    showPageSizeOptions: PropTypes.bool,
    /**
     * The varient of pagination.
     * Simple is `< page of pages >`
     * Full is `[pageSize] N - X of T items << < > >>`
     */
    variant: PropTypes.oneOf(['simple', 'full'])
  }

  static defaultProps = {
    currentPage: 1,
    itemCount: 0,
    itemName: 'item',
    pageSize: 25,
    pageSizeOptions: [25, 50, 100],
    showPageSizeOptions: false,
    variant: 'simple'
  }

  state = {
    pageCount: getTotalPageCount(this.props.itemCount, this.props.pageSize)
  }

  static getDerivedStateFromProps(props, state) {
    const pageCount = getTotalPageCount(props.itemCount, props.pageSize)

    if (pageCount !== state.pageCount) {
      return {
        pageCount
      }
    }

    return null
  }

  getCurrentPage = () => {
    return Math.max(Math.min(this.state.pageCount, this.props.currentPage), 1)
  }

  onRequestFirstPage = () => {
    this.onRequestPageChange(1)
  }

  onRequestLastPage = () => {
    this.onRequestPageChange(this.state.pageCount)
  }

  onRequestNextPage = () => {
    this.onRequestPageChange(this.getCurrentPage() + 1)
  }

  onRequestPrevPage = () => {
    this.onRequestPageChange(this.getCurrentPage() - 1)
  }

  onRequestPageChange = page => {
    const firstPage = 1
    const lastPage = this.state.pageCount
    const isWithinBounds = page >= firstPage && page <= lastPage
    const pageChanged = page !== this.getCurrentPage()

    if (
      pageChanged &&
      isWithinBounds &&
      typeof this.props.onRequestPageChange === 'function'
    ) {
      this.props.onRequestPageChange(page)
    }
  }

  onRequestPageSizeChange = event => {
    if (typeof this.props.onRequestPageSizeChange === 'function') {
      this.props.onRequestPageSizeChange(Number(event.target.value))
    }
  }

  renderFullPagination() {
    const {
      itemCount,
      itemName,
      pageSize,
      pageSizeOptions,
      showPageSizeOptions
    } = this.props
    const { pageCount } = this.state
    const currentPage = this.getCurrentPage()
    const pluralizedName = getItemName(itemName, itemCount)
    const humanizedCount = abbreviateNumber(itemCount)
    const currentPageStart = Math.min(
      (currentPage - 1) * pageSize + 1,
      itemCount
    )
    const currentPageEnd = Math.min(currentPage * pageSize, itemCount)
    const hasPreviousPage = currentPage > 1
    const hasNextPage = currentPage < pageCount

    // if the page doesn't have a range but only has 1 item, display `N` instead of `N-N`
    const pageRange =
      currentPageStart === currentPageEnd
        ? currentPageStart
        : `${currentPageStart}-${currentPageEnd}`

    return (
      <React.Fragment>
        {showPageSizeOptions && (
          <Select
            onChange={this.onRequestPageSizeChange}
            flex="none"
            value={pageSize}
          >
            {pageSizeOptions.map(number => (
              <option key={number} value={number}>
                {number}
              </option>
            ))}
          </Select>
        )}
        <Text
          marginX={majorScale(1)}
          minWidth={majorScale(5)}
          textAlign="center"
        >
          {pageRange} of {humanizedCount} {pluralizedName}
        </Text>
        <IconButton
          icon="double-chevron-left"
          onClick={this.onRequestFirstPage}
          disabled={!hasPreviousPage}
          marginLeft={minorScale(1)}
        />
        <IconButton
          icon="chevron-left"
          onClick={this.onRequestPrevPage}
          disabled={!hasPreviousPage}
          marginLeft={minorScale(1)}
        />
        <IconButton
          icon="chevron-right"
          onClick={this.onRequestNextPage}
          disabled={!hasNextPage}
          marginLeft={minorScale(1)}
        />
        <IconButton
          icon="double-chevron-right"
          onClick={this.onRequestLastPage}
          disabled={!hasNextPage}
          marginLeft={minorScale(1)}
        />
      </React.Fragment>
    )
  }

  renderSimplePagination() {
    const currentPage = this.getCurrentPage()
    const { pageCount } = this.state

    return (
      <React.Fragment>
        <IconButton
          icon="chevron-left"
          height={majorScale(3)}
          onClick={this.onRequestPrevPage}
          disabled={currentPage === 1}
        />
        <Text
          size={300}
          marginX={majorScale(1)}
          minWidth={majorScale(5)}
          textAlign="center"
        >
          {currentPage} of {pageCount}
        </Text>
        <IconButton
          icon="chevron-right"
          height={majorScale(3)}
          onClick={this.onRequestNextPage}
          disabled={currentPage === pageCount}
        />
      </React.Fragment>
    )
  }

  render() {
    const {
      currentPage,
      itemCount,
      itemName,
      onRequestPageChange,
      onRequestPageSizeChange,
      pageSize,
      pageSizeOptions,
      showPageSizeOptions,
      variant,
      ...props
    } = this.props

    return (
      <Pane {...layoutProps} {...props}>
        {variant === 'full'
          ? this.renderFullPagination()
          : this.renderSimplePagination()}
      </Pane>
    )
  }
}

export default Pagination
